import React, { useRef, useState } from "react";
import { Box, Button, IconButton, Stack, Typography } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import { useIsMobile } from "@/hooks/useIsMobile";
import {
  MAX_RELATED_FILE_BYTES,
  MAX_RELATED_FILE_MB,
  MAX_RELATED_FILES,
  MAX_RELATED_FILES_TOTAL_BYTES,
  MAX_RELATED_FILES_TOTAL_MB,
} from "../constants/relatedFiles";
import { SectionCard } from "./formLayout";

interface RelatedFilesSectionProps {
  files: File[];
  onFilesChange: (files: File[]) => void;
}

const allowedExtensions = [".pdf", ".zip"];

const isAllowedFile = (file: File): boolean => {
  const lowerName = file.name.toLowerCase();
  return allowedExtensions.some((extension) => lowerName.endsWith(extension));
};

const isSameFile = (left: File, right: File): boolean =>
  left.name === right.name &&
  left.size === right.size &&
  left.lastModified === right.lastModified;

export const RelatedFilesSection: React.FC<RelatedFilesSectionProps> = ({
  files,
  onFilesChange,
}) => {
  const [error, setError] = useState("");
  const [isDropActive, setIsDropActive] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isMobile = useIsMobile();

  const addFiles = (newFiles: File[]): void => {
    if (newFiles.length === 0) return;
    if (newFiles.some((file) => !isAllowedFile(file))) {
      setError("関連資料はPDFまたはZIPのみ選択できます");
      return;
    }

    const tooLarge = newFiles.find(
      (file) => file.size > MAX_RELATED_FILE_BYTES
    );
    if (tooLarge) {
      setError(
        `${tooLarge.name}: 1件${MAX_RELATED_FILE_MB}MBまでのファイルを選択してください`
      );
      return;
    }

    const uniqueFiles = [...files];
    newFiles.forEach((file) => {
      if (!uniqueFiles.some((existing) => isSameFile(existing, file))) {
        uniqueFiles.push(file);
      }
    });
    if (uniqueFiles.length > MAX_RELATED_FILES) {
      setError(`関連資料は${MAX_RELATED_FILES}件まで選択できます`);
      return;
    }
    const totalBytes = uniqueFiles.reduce((sum, file) => sum + file.size, 0);
    if (totalBytes > MAX_RELATED_FILES_TOTAL_BYTES) {
      setError(`関連資料は合計${MAX_RELATED_FILES_TOTAL_MB}MBまで選択できます`);
      return;
    }

    setError("");
    onFilesChange(uniqueFiles);
  };

  return (
    <SectionCard
      title="関連資料アップロード"
      icon={<AttachFileIcon sx={{ color: "#3b82f6", fontSize: 20 }} />}
      description={`運営へ提供する資料をPDF/ZIP形式で選択してください（任意・${MAX_RELATED_FILES}件まで・1件${MAX_RELATED_FILE_MB}MBまで・合計${MAX_RELATED_FILES_TOTAL_MB}MBまで）。`}
    >
      <Box
        onDragOver={(event) => {
          event.preventDefault();
          setIsDropActive(true);
        }}
        onDragLeave={() => {
          setIsDropActive(false);
        }}
        onDrop={(event) => {
          event.preventDefault();
          setIsDropActive(false);
          addFiles(Array.from(event.dataTransfer.files));
        }}
        sx={{
          p: 2,
          borderRadius: "12px",
          border: isDropActive ? "2px dashed #2563eb" : "2px dashed #cbd5e1",
          backgroundColor: isDropActive ? "#eff6ff" : "#ffffff",
          textAlign: "center",
          transition: "all 0.2s",
        }}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.zip"
          multiple
          aria-label="関連資料を選択"
          style={{ display: "none" }}
          onChange={(event) => {
            addFiles(Array.from(event.target.files ?? []));
            event.target.value = "";
          }}
        />
        {/* label だけだと Tab で選べないため、ボタンから隠した input を開く */}
        <Button
          color="inherit"
          onClick={() => {
            fileInputRef.current?.click();
          }}
          sx={{
            flexDirection: "column",
            width: "100%",
            py: 1.5,
            color: isDropActive ? "#2563eb" : "#94a3b8",
            textTransform: "none",
          }}
        >
          <CloudUploadIcon sx={{ fontSize: 40, mb: 0.5 }} />
          <Typography variant="body2" sx={{ color: "#64748b" }}>
            {isMobile
              ? "ファイルを選択（PDF・ZIP）"
              : "クリックまたはドラッグ＆ドロップでアップロード（PDF・ZIP）"}
          </Typography>
        </Button>
      </Box>
      {error ? (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {error}
        </Typography>
      ) : null}
      {files.length > 0 ? (
        <Stack spacing={1} sx={{ mt: 2 }}>
          {files.map((file, index) => (
            <Stack
              key={`${file.name}-${file.size}-${file.lastModified}`}
              direction="row"
              alignItems="center"
              spacing={1}
            >
              <AttachFileIcon fontSize="small" color="action" />
              <Typography variant="body2" sx={{ flex: 1 }}>
                {file.name}
              </Typography>
              <IconButton
                aria-label={`${file.name}を削除`}
                size="small"
                color="error"
                onClick={() => {
                  onFilesChange(
                    files.filter((_, fileIndex) => fileIndex !== index)
                  );
                }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Stack>
          ))}
        </Stack>
      ) : null}
    </SectionCard>
  );
};
